<template>
    <div class="heading-holder">
        <h2>{{ translate('title') }}</h2>
        <p>{{ translate('description') }}</p>
    </div>

    <div style="opacity: 0; position: absolute">
        <div id="type-all" />
        <div v-for="(value, index) in filterOptions('type')" :id="`type-${value.value}`" :key="index" />
    </div>

    <div ref="filterAdvanced" class="filter-advanced">
        <ul class="tabs-container">
            <li :class="['tab', !filteredColumns.find((column) => column.type === 'type') ? 'selected' : null]">
                <button type="button" @click.prevent="selectSingleFilterOption('all', 'type')">
                    <span>{{ translate('filters.plan_types.all') }}</span>
                </button>
            </li>
            <li
                v-for="(value, index) in filterOptions('type')"
                :key="index"
                :class="[
                    'tab',
                    filteredColumns.find((column) => column.type === 'type')?.values.includes(value.value) ? 'selected' : null
                ]"
            >
                <button type="button" @click.prevent="selectSingleFilterOption(value.value, 'type')">
                    <span>{{ transformValueToReadableText(value.value, 'type') }}</span>
                </button>
            </li>
        </ul>

        <div :class="['filters-container', filtersShown ? 'open' : null]">
            <p>{{ translate('filters.advanced') }}</p>

            <div ref="filterMenu" class="types">
                <template v-for="(type, index) in filterableColumns" :key="index">
                    <div v-if="type !== 'type'" class="type">
                        <button type="button" class="button-selection" @click.prevent="toggleFilter(type)">
                            <span
                                v-if="
                                    Object.keys(filteredColumns).length && filteredColumns.find((column) => column.type === type)
                                "
                            >
                                {{ translate(`filters.columns.${type}`) }} ({{
                                    Object.keys(filteredColumns.find((column) => column.type === type).values).length
                                }})
                            </span>
                            <span v-else>{{ translate(`filters.columns.${type}`) }}</span>
                            <i class="icon icon-arrow-down"></i>
                        </button>

                        <ul v-if="filterMenuOpened === type" class="options">
                            <li v-for="option in filterOptions(type)" :key="option.id" class="option">
                                <button
                                    type="button"
                                    :class="[
                                        'button-option',
                                        filteredColumns.find((column) => column.type === type)?.values.includes(option.value)
                                            ? 'selected'
                                            : null
                                    ]"
                                    @click.prevent="selectFilterOption(option.value, type)"
                                >
                                    <span class="checkbox" />
                                    <span class="value" v-html="transformValueToReadableText(option.value, type)" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
        </div>

        <button type="button" class="button-toggle-filters" @click.prevent="toggleFiltersContainer()">
            <i class="icon icon-sliders" />
            <span>{{ filtersShown ? translate('filters.hide_filters') : translate('filters.show_filters') }}</span>
        </button>
    </div>

    <div class="filter-billing-cycle">
        <p>{{ translate('filters.simple') }}</p>

        <div ref="selectionMenu" :class="['billing-cycle', selectionMenuOpened ? 'open' : null]">
            <button type="button" class="button-selection" @click.prevent="toggleSelection()">
                <span>{{ translate(`filters.billing_cycles.${selectionValue}`) }}</span>
                <i class="icon icon-arrow-down"></i>
            </button>

            <ul v-if="selectionMenuOpened" class="options">
                <li v-for="cycle in billingCycles" :key="cycle" class="option">
                    <button
                        type="button"
                        :class="['button-option', cycle === selectionValue ? 'selected' : null]"
                        @click.prevent="selectBillingCycle(cycle)"
                    >
                        {{ translate(`filters.billing_cycles.${cycle}`) }}
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <div class="table">
        <table>
            <thead>
                <tr>
                    <th v-for="column in columns" :key="column">
                        {{ translate(`filters.columns.${column}`) }}
                    </th>
                    <th>
                        <img
                            src="/images/ddos-protected-blue-stroke.svg"
                            class="protection"
                            alt=""
                            width="100"
                            height="100"
                            loading="lazy"
                        />
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-if="filteredPlans.length">
                    <tr v-for="plan in filteredPlans" :key="plan.id">
                        <td v-for="column in columns" :key="column">
                            <template v-if="column === 'prices'">
                                <div class="column-value-container price-container">
                                    <template
                                        v-if="plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).old_price"
                                    >
                                        <ul>
                                            <li>
                                                <span class="price-discounted">
                                                    {{
                                                        translate(`filters.billing_cycles_with_price.${selectionValue}`, {
                                                            price: formatPrice(
                                                                Number(
                                                                    plan.prices.find(
                                                                        (cycle) => cycle.billing_cycle === selectionValue
                                                                    ).price
                                                                ),
                                                                currency,
                                                                locale
                                                            )
                                                        })
                                                    }}
                                                </span>
                                                <span class="price-base">
                                                    {{
                                                        translate(`filters.billing_cycles_with_price.${selectionValue}`, {
                                                            price: formatPrice(
                                                                plan.prices.find(
                                                                    (cycle) => cycle.billing_cycle === selectionValue
                                                                ).old_price,
                                                                currency,
                                                                locale
                                                            )
                                                        })
                                                    }}
                                                </span>
                                            </li>
                                        </ul>
                                    </template>

                                    <template v-else>
                                        <span class="price-original">
                                            {{
                                                translate(`filters.billing_cycles_with_price.${selectionValue}`, {
                                                    price: formatPrice(
                                                        plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).price,
                                                        currency,
                                                        locale
                                                    )
                                                })
                                            }}
                                        </span>
                                    </template>
                                </div>
                            </template>

                            <template v-else>
                                <template v-if="['cpu', 'ram', 'storage', 'traffic'].includes(column)">
                                    <span class="column-label">{{ translate(`filters.columns.${column}`) }}:</span>
                                    <div class="column-value-container">
                                        <span class="column-value">
                                            {{ transformValueToReadableText(plan.metadata[column], column) }}
                                        </span>

                                        <template v-if="column === 'cpu' && plan.metadata.benchmark">
                                            <span class="column-value-note">
                                                {{
                                                    translate('table.benchmark', {
                                                        score: new Intl.NumberFormat(locale).format(plan.metadata.benchmark)
                                                    })
                                                }}

                                                <i class="icon icon-info" :title="translate('table.benchmark_note')"></i>
                                            </span>
                                        </template>
                                    </div>
                                </template>

                                <template v-else-if="column === 'plan'">
                                    <div class="column-value-container plan-container">
                                        <template
                                            v-if="
                                                plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).discount > 10
                                            "
                                        >
                                            <span class="badge promo">
                                                {{ translate('table.badge.promo') }}
                                            </span>
                                        </template>

                                        <template v-if="plan.metadata.regions">
                                            <ul>
                                                <li v-for="region in plan.metadata.regions" :key="region.country">
                                                    <img
                                                        :src="`/images/flags/${region.country}.svg`"
                                                        :title="transformRegionToLocation(region)"
                                                        :class="region.status !== 'available' ? region.status : null"
                                                        width="640"
                                                        height="320"
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                </li>
                                            </ul>
                                        </template>

                                        <h3>{{ plan.plan }}</h3>
                                    </div>
                                </template>

                                <template v-else>
                                    {{ plan.metadata[column] }}
                                </template>
                            </template>
                        </td>
                        <td>
                            <a
                                :href="`${clientArea}cart/cloud-servers/?product_code=${
                                    plan.hostbill_code
                                }&cycle=${selectionValue.charAt(0)}`"
                                @click.prevent="
                                    makeGtmEvent(
                                        gtmEventName,
                                        { name: 'purchase', plan: plan.plan },
                                        `${clientArea}cart/cloud-servers/?product_code=${
                                            plan.hostbill_code
                                        }&cycle=${selectionValue.charAt(0)}`
                                    )
                                "
                            >
                                <span>{{ translate('table.button') }}</span>

                                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                                    <g fill-rule="evenodd">
                                        <path class="line-path" d="M0 5h7"></path>
                                        <path class="tip-path" d="M1 1l4 4-4 4"></path>
                                    </g>
                                </svg>
                            </a>
                        </td>
                    </tr>
                </template>

                <template v-else>
                    <tr class="no-results">
                        <td :colspan="columns.length">
                            {{ translate('table.no_results') }}
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>

        <div class="notes" v-html="translate('notes', { client_area: clientArea, contacts })" />
    </div>
</template>

<script setup>
    import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

    import useFormatPrice from '../composables/useFormatPrice'
    import { useTranslate } from '../composables/useTranslate'

    const props = defineProps({
        /**
         * The locale to use for the translations.
         *
         * @type {String}
         * @required
         */
        locale: {
            type: String,
            required: true
        },
        /**
         * The currency to use for the prices.
         *
         * @type {String}
         * @required
         */
        currency: {
            type: String,
            required: true
        },
        /**
         * The plans to display in the table.
         *
         * @type {Object}
         * @required
         */
        plans: {
            type: Object,
            required: true
        },
        /**
         * The URL of the client area.
         *
         * @type {String}
         * @required
         */
        clientArea: {
            type: String,
            required: true
        },
        /**
         * The contacts URL.
         *
         * @type {String}
         * @required
         */
        contacts: {
            type: String,
            required: true
        },
        /**
         * The available billing cycles to choose from.
         *
         * @type {Array}
         * @required
         */
        billingCycles: {
            type: Array,
            required: true
        }
    })

    /**
     * The translation strings for the component.
     *
     * @type {Object}
     */
    const translations = {
        bg: {
            title: 'Цени на плановете',
            description:
                'Изберете Cloud VPS план според Вашите нужди - Basic Cloud, CPU Optimized или Memory Optimized. Използвайте предоставените филтри, за да видите цената на желаната от Вас услуга. Поръчайте Cloud VPS в няколко лесни стъпки.',
            notes: '<p>Cloud VPS е неменажирана услуга. Всички дейности по конфигуриране и управление на виртуалния сървър се извършват от клиента. При нужда от системна администрация, можете да се свържете с <a href="{contacts}" rel="noopener">отдел "Продажби"</a>.</p><p>Можете да добавите още дисково пространство (до 2000 GB) към избрания план, след като се впишете в <a href="{client_area}" target="_blank" rel="noopener">клиентската зона</a>.</p>',
            filters: {
                hide_filters: 'Сгъване на филтрите',
                show_filters: 'Разгъване на филтрите',
                simple: 'Изберете период на плащане:',
                advanced: 'Филтриране по:',
                columns: {
                    type: 'Тип',
                    regions: 'Регион',
                    plan: 'План',
                    cpu: 'Процесорни ядра',
                    ram: 'RAM памет',
                    storage: 'Диск (NVMe)',
                    traffic: 'Трафик на данни',
                    prices: 'Цена'
                },
                plan_types: {
                    all: 'Всички планове',
                    basic: 'Basic Cloud',
                    cpu_optimized: 'CPU Optimized',
                    memory_optimized: 'Memory Optimized',
                    storage_optimized: 'Storage Optimized'
                },
                region: {
                    bg: '<span>България</span> ({data_centers})',
                    de: '<span>Германия</span> ({data_centers})',
                    gb: '<span>Великобритания</span> ({data_centers})'
                },
                billing_cycles: {
                    free: 'Безплатен',
                    one_time: 'Еднократен',
                    weekly: 'Ежеседмично',
                    monthly: 'Месечен',
                    quarterly: 'На всеки 3 месеца',
                    semi_annually: 'На всеки 6 месеца',
                    annually: 'Годишен (спестявате 10%)',
                    biennially: 'На всеки 2 години',
                    triennially: 'На всеки 3 години'
                },
                billing_cycles_with_price: {
                    one_time: '{price} еднократно',
                    weekly: '{price} ежеседмично',
                    monthly: '{price} на месец',
                    quarterly: '{price} на всеки 3 месеца',
                    semi_annually: '{price} на всеки 6 месеца',
                    annually: '{price} на година',
                    biennially: '{price} на всеки 2 години',
                    triennially: '{price} на всеки 3 години'
                }
            },
            table: {
                benchmark: 'Benchmark: {score}',
                benchmark_note: 'Този резултат е базиран на тестове на производителността, направени чрез Geekbench 6.',
                button: 'Поръчайте',
                no_results: 'Няма намерени резултати за избраните опции.',
                region: {
                    bg: 'България',
                    de: 'Германия',
                    gb: 'Великобритания'
                },
                region_extended: {
                    'eu-sof': 'София, България ({data_center})',
                    'eu-fra': 'Франкфурт, Германия ({data_center})',
                    'eu-lon': 'Лондон, Великобритания ({data_center})'
                },
                badge: {
                    promo: 'промо'
                },
                status: {
                    soon: 'скоро'
                }
            }
        },
        en: {
            title: 'Prices',
            description:
                'Choose a Cloud VPS plan according to your needs - Basic Cloud, CPU Optimized or Memory Optimized. Use the provided filters to see the price of the service you want. Order Cloud VPS in a few easy steps.',
            notes: '<p>Cloud VPS is an unmanaged service. All configuration and management of the virtual server are performed by the customer. If you require system administration, please contact our <a href="{contacts}" rel="noopener">Sales Department</a>.</p><p>You can add more disk space (up to 2000 GB) to the selected plan after you log in to the <a href="{client_area}" target="_blank" rel="noopener">client area</a>.</p>',
            filters: {
                hide_filters: 'Hide Filters',
                show_filters: 'Show Filters',
                simple: 'Select a billing cycle:',
                advanced: 'Filter by:',
                columns: {
                    type: 'Type',
                    regions: 'Region',
                    plan: 'Plan',
                    cpu: 'CPU Cores',
                    ram: 'RAM',
                    storage: 'Disk (NVMe)',
                    traffic: 'Traffic',
                    prices: 'Price'
                },
                plan_types: {
                    all: 'All Plans',
                    basic: 'Basic Cloud',
                    cpu_optimized: 'CPU Optimized',
                    memory_optimized: 'Memory Optimized',
                    storage_optimized: 'Storage Optimized'
                },
                region: {
                    bg: 'Bulgaria ({data_centers})',
                    de: 'Germany ({data_centers})',
                    gb: 'United Kingdom ({data_centers})'
                },
                billing_cycles: {
                    free: 'Free',
                    one_time: 'One Time',
                    weekly: 'Weekly',
                    monthly: 'Monthly',
                    quarterly: 'Quarterly',
                    semi_annually: 'Semi-Annually',
                    annually: 'Annually (save 10%)',
                    biennially: 'Biennially',
                    triennially: 'Triennially'
                },
                billing_cycles_with_price: {
                    one_time: '{price} one time',
                    weekly: '{price} weekly',
                    monthly: '{price} monthly',
                    quarterly: '{price} quarterly',
                    semi_annually: '{price} semi-annually',
                    annually: '{price} annually',
                    biennially: '{price} biennially',
                    triennially: '{price} triennially'
                }
            },
            table: {
                benchmark: 'Benchmark: {score}',
                benchmark_note: 'This result is based on performance tests conducted using Geekbench 6.',
                button: 'Order Now',
                no_results: 'No results found for the selected options.',
                region: {
                    bg: 'Bulgaria',
                    de: 'Germany',
                    gb: 'United Kingdom'
                },
                region_extended: {
                    'eu-sof': 'Sofia, Bulgaria ({data_center})',
                    'eu-fra': 'Frankfurt, Germany ({data_center})',
                    'eu-lon': 'London, United Kingdom ({data_center})'
                },
                badge: {
                    promo: 'promo'
                },
                status: {
                    soon: 'soon'
                }
            }
        }
    }

    const { translate } = useTranslate(props.locale, translations)
    const { formatPrice } = useFormatPrice()

    const selectionMenu = ref(null) // The selection dropdown menu element.
    const selectionMenuOpened = ref(false) // Whether the selection dropdown menu is opened.
    const selectionValue = ref('monthly') // The selected billing cycle by default.
    const columns = ['plan', 'cpu', 'ram', 'storage', 'traffic', 'prices'] // The columns to display in the table.
    const filterableColumns = ref(['type', 'regions', 'cpu', 'ram', 'storage', 'traffic']) // The columns that can be filtered.
    const filterAdvanced = ref(null) // The advanced filters container element.
    const filterMenu = ref(null) // The filter dropdown menu element.
    const filterMenuOpened = ref(null) // Whether the filter dropdown menu is opened.
    const filteredColumns = ref([]) // The columns and their values that are filtered.
    const filtersShown = ref(false) // Whether the advanced filters are shown.
    const gtmEventName = 'cloud_vps_link' // The GTM event name.

    /**
     * Scroll to a given element on the page.
     *
     * @param {Element} element - The element to scroll to.
     */
    const scrollToElement = (element) => {
        const headerElement = document.querySelector('.page-header')

        const elementOffsetTop = element.getBoundingClientRect().top + window.pageYOffset
        const headerHeight = headerElement ? headerElement.offsetHeight : 0

        window.scrollTo({
            top: Number(elementOffsetTop - headerHeight) - 20,
            behavior: 'smooth'
        })
    }

    /**
     * Toggle the visibility of the billing cycle selection menu.
     */
    const toggleSelection = () => {
        selectionMenuOpened.value = !selectionMenuOpened.value
    }

    /**
     * Toggle the visibility of the filter dropdown menu for a given column type.
     */
    const toggleFilter = (type) => {
        filterMenuOpened.value = filterMenuOpened.value === type ? null : type
    }

    /**
     * Toggle the visibility of the advanced filters container.
     */
    const toggleFiltersContainer = () => {
        filtersShown.value = !filtersShown.value

        setTimeout(() => {
            scrollToElement(filterAdvanced.value)
        }, 100)
    }

    /**
     * Extract unique values for a given column type.
     *
     * @param {String} type - The column type.
     * @return {Array} - Unique values for the column type.
     */
    const extractUniqueValues = (type) => {
        if (!props.plans.length) return []
        if (!filterableColumns.value.includes(type)) return []

        const uniqueValues = new Set()

        props.plans.forEach((plan) => {
            if (!plan.metadata[type]) return

            if (plan.metadata[type] instanceof Array) {
                plan.metadata[type].forEach((value) => uniqueValues.add(value.country))
                return
            }

            uniqueValues.add(plan.metadata[type])
        })

        return Array.from(uniqueValues).sort((a, b) => a - b)
    }

    /**
     * Extract unique region values for a given region code.
     *
     * @param {String} code - The region code.
     * @return {String} - Unique region values.
     */
    const extractUniqueRegionValues = (code) => {
        if (!props.plans.length) return []

        const uniqueValues = new Set()

        props.plans.forEach((plan) => {
            if (!plan.metadata.regions) return

            plan.metadata.regions.forEach((region) => {
                if (region.country === code) {
                    uniqueValues.add(region.name)
                }
            })
        })

        let values = Array.from(uniqueValues).sort((a, b) => a - b)

        return values.toString().replace(/,/g, ', ')
    }

    /**
     * Get the filter options for a given column type.
     *
     * @param {String} type - The column type.
     * @return {Array} - The filter options.
     */
    const filterOptions = (type) => {
        return extractUniqueValues(type).map((value) => ({ value }))
    }

    /**
     * Computed property to filter plans based on selected filter options.
     */
    const filteredPlans = computed(() => {
        if (filteredColumns.value.length === 0) {
            return props.plans
        }

        return props.plans.filter((plan) => {
            return filteredColumns.value.every((column) => {
                if (column.type === 'regions') {
                    return column.values.some((value) => {
                        return plan.metadata.regions.some((region) => region.country === value)
                    })
                }

                return column.values.includes(plan.metadata[column.type])
            })
        })
    })

    /**
     * Utility function to transform the value of a column to a more readable text.
     *
     * @param {String|Number} value - The value to transform.
     * @param {String} type - The type of the value.
     * @return {String} - The transformed value.
     */
    const transformValueToReadableText = (value, type) => {
        switch (type) {
            case 'type':
                return translate(`filters.plan_types.${value}`)
            case 'regions':
                return translate(`filters.region.${value}`, { data_centers: extractUniqueRegionValues(value) })
            case 'cpu':
                return `${value} vCPU`
            case 'ram':
                return `${value} GB`
            case 'storage':
                return `${value} GB`
            case 'traffic':
                return `${value} TB`
            default:
                return value
        }
    }

    /**
     * Utility function to make the region value more readable.
     *
     * @param {Object} region - The region object.
     * @return {String} - Readable string representation of the region.
     */
    const transformRegionToLocation = (region) => {
        const regionStatus = (value) => {
            if (value !== 'available') {
                return ` (${translate(`table.status.${value}`)})`
            }

            return ''
        }

        return `${translate(`table.region_extended.${region.city}`, { data_center: region.name })}${regionStatus(region.status)}`
    }

    /**
     * Get the number of months in a billing cycle.
     *
     * @param {String} cycle - The billing cycle.
     * @return {Number} - The number of months in the billing cycle.
     */
    const transformBillingCycleToMonths = (cycle) => {
        switch (cycle) {
            case 'weekly':
                return 4
            case 'monthly':
                return 1
            case 'quarterly':
                return 3
            case 'semi_annually':
                return 6
            case 'annually':
                return 12
            case 'biennially':
                return 24
            case 'triennially':
                return 36
            default:
                return 1
        }
    }

    /**
     * Clear all filter options for a given type.
     *
     * @param {String} type - The type of the filter options to clear.
     */
    const clearFilterOptionsByType = (type) => {
        filteredColumns.value = filteredColumns.value.filter((column) => column.type !== type)
    }

    /**
     * Select a billing cycle and close the selection menu.
     *
     * @param {String} option - The selected billing cycle.
     */
    const selectBillingCycle = (option) => {
        if (option === selectionValue.value) return

        if (!props.billingCycles.includes(option)) {
            console.error(`The option "${option}" is not valid. The available options are: ${props.billingCycles.join(', ')}`)
            return
        }

        selectionValue.value = option
        selectionMenuOpened.value = false
    }

    /**
     * Select a filter option and update the filteredColumns array.
     *
     * @param {String|Number} optionId - The ID of the selected option.
     * @param {String} optionType - The type of the selected option.
     */
    const selectFilterOption = (optionId, optionType) => {
        // Find the existing entry for the current type in filteredColumns
        let column = filteredColumns.value.find((col) => col.type === optionType)

        if (column) {
            // Check if the option is already selected
            const optionIndex = column.values.indexOf(optionId)

            if (optionIndex > -1) {
                // If the option is already selected, remove it from the values array
                column.values.splice(optionIndex, 1)

                // If no values are left for this type, remove the entire type from filteredColumns
                if (column.values.length === 0) {
                    filteredColumns.value = filteredColumns.value.filter((col) => col.type !== optionType)
                }
            } else {
                // If the option is not selected, add it to the values array
                column.values.push(optionId)
            }
        } else {
            // If the type does not exist in filteredColumns, create a new entry
            filteredColumns.value.push({
                type: optionType,
                values: [optionId]
            })
        }
    }

    /**
     * Select a single filter option and clear all other options for the same type.
     *
     * @param {String|Number} optionId - The ID of the selected option.
     * @param {String} optionType - The type of the selected option.
     */
    const selectSingleFilterOption = (optionId = null, optionType) => {
        clearFilterOptionsByType(optionType)

        if (optionType === 'type') {
            if (optionId !== 'all') {
                filteredColumns.value.push({
                    type: optionType,
                    values: [optionId]
                })
            }

            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                event: gtmEventName,
                name: 'tab',
                tab: optionId
            })

            window.history.pushState(null, null, `#type-${optionId}`)
        }
    }

    /**
     * Make a Google Tag Manager event when clicking a button.
     *
     * @param {String} event - The GTM event name.
     * @param {String} data - The GTM event data.
     * @param {String} custom_link - Whether to open a custom link in a new tab.
     * @return {String} - The GTM event string.
     */
    const makeGtmEvent = (event, data, custom_link = null) => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event, ...data })

        if (custom_link) {
            window.open(custom_link, '_blank')
        }

        return true
    }

    /**
     * Close the selection dropdown menu when clicking outside it.
     *
     * @param {Event} event
     */
    const handleClickOutside = (event) => {
        if (selectionMenu.value && !selectionMenu.value.contains(event.target)) {
            selectionMenuOpened.value = false
        }

        if (filterMenuOpened.value && !filterMenu.value.contains(event.target)) {
            filterMenuOpened.value = null
        }
    }

    window.addEventListener('select-tab', (event) => {
        if (event.detail.tab && filterOptions('type').find((option) => option.value === event.detail.tab)) {
            selectSingleFilterOption(event.detail.tab, 'type')
        }
    })

    /**
     * Add event listener for clicking outside the selection dropdown menu.
     */
    onMounted(() => {
        document.addEventListener('click', handleClickOutside)

        if (window.location.hash) {
            const type = window.location.hash.replace('#type-', '')

            if (type && filterOptions('type').find((option) => option.value === type)) {
                selectSingleFilterOption(type, 'type')
            }
        }
    })

    /**
     * Remove event listener for clicking outside the selection dropdown menu.
     */
    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside)
    })
</script>
